import { Component, Input, forwardRef } from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import {
  DateRange,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { EntireWeekRangeSelectionStrategy } from '../../../adapters';
import { ControlValueAccessorComponent } from '../../../core';
import { CALENDAR_VIEW_MODE, CalendarView } from '../../../models';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ComparatorUtils, DateTimeUtils } from '../../../utils';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { PipesModule } from '../../../pipes';
import { LibCoreLayoutModule } from '../../core-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'lib-calendar-date-picker',
  templateUrl: './calendar-date-picker.component.html',
  styleUrl: './calendar-date-picker.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    PipesModule,
    LibCoreLayoutModule,
    // Material design
    MatButtonModule,
    MatDatepickerModule,
    MatIconModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CalendarDatePickerComponent),
      multi: true,
    },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: EntireWeekRangeSelectionStrategy,
    },
  ],
})
export class CalendarDatePickerComponent
  extends ControlValueAccessorComponent
  implements ControlValueAccessor
{
  public readonly CALENDAR_VIEW_MODE = CALENDAR_VIEW_MODE;
  public readonly FORM_NAME = {
    VIEW_MODE: 'viewMode',
    FROM_DATE: 'fromDate',
    TO_DATE: 'toDate',
  };

  @Input() isLoading = false;
  @Input() minDate: Date;
  @Input() maxDate: Date;

  public formGroup: FormGroup;
  public isOpenCalendarPicker = false;

  constructor() {
    super();

    this.formGroup = new FormGroup({
      [this.FORM_NAME.VIEW_MODE]: new FormControl(CALENDAR_VIEW_MODE.DAY, [
        Validators.required,
      ]),
      [this.FORM_NAME.FROM_DATE]: new FormControl(null, [Validators.required]),
      [this.FORM_NAME.TO_DATE]: new FormControl(null, [Validators.required]),
    });

    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.componentDestroyed),
        distinctUntilChanged(ComparatorUtils.generateComparatorForObject()),
        debounceTime(300)
      )
      .subscribe((value: CalendarView) => {
        if (this.formGroup.valid) {
          this._onChange(value);
        }
      });
  }

  public isDisable(): boolean {
    return this.formGroup.disabled || this.isLoading;
  }

  public getSelectedDateForCalendarPicker(): DateRange<Date> | Date {
    return this.getCurrentViewMode() === CALENDAR_VIEW_MODE.WEEK ||
      this.getCurrentViewMode() === CALENDAR_VIEW_MODE.AGENDA
      ? new DateRange(this.getCurrentFromDate(), this.getCurrentToDate())
      : this.getCurrentFromDate();
  }

  public isAbleToSelectPrevious(): boolean {
    return (
      !this.isDisable() &&
      (!this.minDate ||
        this.getCurrentToDate() > DateTimeUtils.getEndOfDate(this.minDate))
    );
  }

  public previous(): void {
    if (!this.isAbleToSelectPrevious()) {
      return;
    }

    if (this.getCurrentViewMode() === CALENDAR_VIEW_MODE.DAY) {
      this.selectDate(
        DateTimeUtils.addDayToDate(this.getCurrentFromDate(), -1)
      );
    } else if (this.getCurrentViewMode() === CALENDAR_VIEW_MODE.MONTH) {
      this.selectDate(
        DateTimeUtils.getFirstDateOfPreviousMonth(this.getCurrentFromDate())
      );
    } else {
      this.selectDate(
        DateTimeUtils.getFirstDateOfPreviousWeek(this.getCurrentFromDate())
      );
    }
  }

  public isAbleToSelectNext(): boolean {
    return (
      !this.isDisable() &&
      (!this.maxDate ||
        this.getCurrentFromDate() < DateTimeUtils.getBeginOfDate(this.maxDate))
    );
  }

  public next(): void {
    if (!this.isAbleToSelectNext()) {
      return;
    }

    if (this.getCurrentViewMode() === CALENDAR_VIEW_MODE.DAY) {
      this.selectDate(DateTimeUtils.addDayToDate(this.getCurrentFromDate(), 1));
    } else if (this.getCurrentViewMode() === CALENDAR_VIEW_MODE.MONTH) {
      this.selectDate(
        DateTimeUtils.getFirstDateOfNextMonth(this.getCurrentFromDate())
      );
    } else {
      this.selectDate(
        DateTimeUtils.getFirstDateOfNextWeek(this.getCurrentFromDate())
      );
    }
  }

  public selectCurrentDateAndCloseCalendarPicker(): void {
    this.selectDateAndCloseCalendarPicker(new Date());
  }

  public selectMonthAndCloseCalendarPicker(selectedDate: Date): void {
    if (this.getCurrentViewMode() !== CALENDAR_VIEW_MODE.MONTH) {
      return;
    }
    this.selectDateAndCloseCalendarPicker(selectedDate);
  }

  public selectDateAndCloseCalendarPicker(selectedDate: Date): void {
    this.isOpenCalendarPicker = false;
    this.selectDate(selectedDate);
  }

  private selectDate(selectedDate: Date): void {
    this.selectDateAndViewMode(selectedDate, this.getCurrentViewMode());
  }

  public getCurrentViewMode(): CALENDAR_VIEW_MODE {
    return this.formGroup.get(this.FORM_NAME.VIEW_MODE)!.value;
  }

  public getCurrentFromDate(): Date {
    return this.formGroup.get(this.FORM_NAME.FROM_DATE)!.value;
  }

  public getCurrentToDate(): Date {
    return this.formGroup.get(this.FORM_NAME.TO_DATE)!.value;
  }

  private selectDateAndViewMode(
    selectedDate: Date,
    viewMode: CALENDAR_VIEW_MODE
  ): void {
    switch (viewMode) {
      case CALENDAR_VIEW_MODE.DAY:
        this.formGroup.patchValue({
          viewMode,
          fromDate: DateTimeUtils.getBeginOfDate(selectedDate),
          toDate: DateTimeUtils.getEndOfDate(selectedDate),
        });
        break;
      case CALENDAR_VIEW_MODE.MONTH:
        this.formGroup.patchValue({
          viewMode,
          fromDate: DateTimeUtils.getBeginOfDate(
            DateTimeUtils.getFirstDateOfMonth(selectedDate)
          ),
          toDate: DateTimeUtils.getEndOfDate(
            DateTimeUtils.getLastDateOfMonth(selectedDate)
          ),
        });
        break;
      default:
        this.formGroup.patchValue({
          viewMode,
          fromDate: DateTimeUtils.getBeginOfDate(
            DateTimeUtils.getFirstDateOfWeek(selectedDate)
          ),
          toDate: DateTimeUtils.getEndOfDate(
            DateTimeUtils.getLastDateOfWeek(selectedDate)
          ),
        });
    }
  }

  // required for NG_VALUE_ACCESSOR
  writeValue(calendarView: CalendarView) {
    const currentValue: CalendarView = this.formGroup.value;
    if (
      currentValue?.viewMode !== calendarView?.viewMode ||
      !DateTimeUtils.isSameDate(currentValue?.fromDate, calendarView?.fromDate)
    ) {
      this.selectDateAndViewMode(
        calendarView?.fromDate,
        calendarView?.viewMode
      );
    }
  }

  // required for NG_VALUE_ACCESSOR
  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
    }
  }
}
